// The returned contract's client (module 10 U26; milestone 10 check 11; B429).
//
// MIRROR OF apps/api/src/review/view.ts's ReviewPageView and apps/api/src/review/answer.ts's
// AnswerResult, field for field: a copy rather than an import because the two packages do not
// depend on one another. Kept here, not in ../api.ts, which another milestone holds (the main
// session wires it at merge if it wants it there).
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';

export interface ChipView { ref: string; id: string; kind: string; label: string; at: string | null; signalId: string | null; text: string }
export interface UnitView {
  index: number; kind: 'unchanged' | 'changed' | 'removed' | 'added'; label: string; oldText: string | null; newText: string | null;
  diff: Array<{ op: 'equal' | 'delete' | 'insert'; text: string; tracked: boolean | null }> | null;
  tracking: 'tracked' | 'not_tracked' | null; moved: boolean; renumbered: boolean; trackedBy: Array<{ author: string | null; date: string | null }>;
}
export type Verdict = 'accept' | 'your_call' | 'against_what_was_said';
export interface ReviewView {
  id: string;
  state: 'strong' | 'weak' | 'asked';
  organisation: string | null;
  returned: { name: string; arrivedAt: string; from: string | null; subject: string | null };
  answers: { name: string; at: string; by: string | null; source: 'thread' | 'organisation' | 'contracts'; chosenBy: string | null; chosenAt: string | null } | null;
  question: { text: string; choices: Array<{ candidateId: string; name: string | null; at: string; by: string | null; source: string; score: number }> } | null;
  comparison: { mode: 'clauses' | 'paragraphs'; counts: Record<string, number>; units: UnitView[] } | null;
  reading: {
    changes: Array<{ unitIndex: number; label: string; verdict: Verdict; reasoning: string; evidence: ChipView[] }>;
    said: Array<{ topic: string; line: string; evidence: ChipView[] }>;
    proposals: Array<{ label: string | null; line: string; evidence: ChipView[] }>;
    used: Record<string, number>;
    model: string;
    costUsd: number;
    readAt: string;
  } | null;
  footer: { who: string; did: string[] };
}
export interface ReviewPageView { signalId: string; reviews: ReviewView[] }
export type AnswerResult = { ok: true; reviewId: string; counts: Record<string, number>; hash: string };

async function json<T>(r: Response): Promise<T> {
  if (r.status === 401) throw new Unauthenticated();
  const body = await r.json().catch(() => ({})) as { error?: string };
  if (!r.ok) throw new ApiError(r.status, body.error ?? `HTTP ${r.status}`);
  return body as T;
}

export const review = {
  /** The reviews of one mail's attachments, as the signed-in person may read them; `reviews: []` when there is none they may read. */
  ofSignal: (store: string, signalId: string): Promise<ReviewPageView> =>
    fetch(`/api/review/signal/${encodeURIComponent(signalId)}?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<ReviewPageView>(r)),
  /** "Which document does this answer?" answered with one of the choices offered. */
  answer: (store: string, reviewId: string, candidateId: string): Promise<AnswerResult> =>
    fetch(`/api/review/${encodeURIComponent(reviewId)}/answer`, {
      method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store, candidateId }),
    }).then((r) => json<AnswerResult>(r)),
};
